import { useTranslation } from 'react-i18next';

import type { ScheduleMember } from '@nucleus-commerce/api-client/admin';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useRemoveScheduleMember } from '../hooks/use-member-mutations';

import { ScheduleErrorAlert } from './schedule-error-alert';

export interface RemoveMemberDialogProps {
  scheduleId: string;
  member: ScheduleMember | null;
  onOpenChange: (open: boolean) => void;
  /** It was live somewhere, so the remove ran a release run: offer to open it. */
  onReleased?: (runId: string) => void;
  /** A rule member: the backend excluded it instead, since the rules would add it back. */
  onExcludedInstead?: (member: ScheduleMember) => void;
}

/** Takes a product out of the pool. A live one is unpublished; its store products stay. */
export function RemoveMemberDialog({
  scheduleId,
  member,
  onOpenChange,
  onReleased,
  onExcludedInstead,
}: RemoveMemberDialogProps) {
  const { t } = useTranslation(['schedules', 'common']);
  const remove = useRemoveScheduleMember(scheduleId);
  const live = member?.placements.some((p) => p.state === 'live') ?? false;
  return (
    <AlertDialog
      open={member !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('schedules:pool.remove.title', { title: member?.product.title ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {member?.source === 'rule'
              ? t('schedules:pool.remove.rule')
              : live
                ? t('schedules:pool.remove.live')
                : t('schedules:pool.remove.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? <ScheduleErrorAlert error={remove.error} action="removeMember" /> : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              event.preventDefault();
              if (!member) return;
              remove.mutate(member.productId, {
                onSuccess: ({ releaseRunId, excluded }) => {
                  onOpenChange(false);
                  if (excluded) onExcludedInstead?.(member);
                  else toast.success(t('schedules:toasts.memberRemoved'));
                  if (releaseRunId) onReleased?.(releaseRunId);
                },
              });
            }}
          >
            {t('schedules:pool.remove.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
